import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["LinkComponent"],
  _excluded2 = ["active"],
  _excluded3 = ["titleText"],
  _excluded4 = ["open"],
  _excluded5 = ["children", "item", "pathname"];
import { useState } from 'react';
import styled from 'styled-components';
import UIExpandableSection from 'UIComponents/accordion/UIExpandableSection';
import { CALYPSO, CALYPSO_LIGHT, FLINT, OBSIDIAN, OLAF } from 'HubStyleTokens/colors';
import UILink from 'UIComponents/link/UILink';
import UIRouterNavLink from 'ui-addon-react-router-dom/UIRouterNavLink';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIList from 'UIComponents/list/UIList';
import UIExpandableContents from 'UIComponents/accordion/UIExpandableContents';
import { useLocation } from 'react-router-dom';
import { ExpandableOpener, ExpandableHeader } from './ExpandableComponents';
import { getLocalizedBadge } from '../utils/localization';
import useLocale from 'developer-docs-shared-components/hooks/useLocale';
import { isUrlLocalized } from '../utils/localization';
import { getEnglishBaseUrl } from '../utils/urls';
import { NESTED_NAV_ACTIVE_LINK_CLASSNAME } from '../utils/css';
import { useSelector } from 'react-redux';
import { selectUserGates } from '../selectors/userState';
import { isDocGatedAndDoesUserNotHaveAccess } from '../utils/gates';
import { View } from 'foundations-components/transitional/components/View';
import { Flex } from 'foundations-components/components/Flex';

/** @internal */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
/**
 * Shared link styles for links in the `NavConfigListItem`. Pass in a
 * `LinkComponent` for a router-aware `UIRouterNavLink` or a `UILink`
 * for non-router-aware links.
 */
export const NavConfigListItemLink = styled(_ref => {
  let {
      LinkComponent
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx(LinkComponent, Object.assign({}, rest));
}).withConfig({
  displayName: "NavConfigListItem__NavConfigListItemLink",
  componentId: "sc-1vznng5-0"
})(["display:block;font-size:14px;padding:4px 24px;color:", ";font-weight:500;&:hover{text-decoration:none;color:", ";}&.", "{background-color:", ";border-radius:3px;position:relative;font-weight:600;&::before{content:'';border-left:4px solid ", ";height:100%;left:0;position:absolute;top:0;border-radius:3px;}}"], OBSIDIAN, OBSIDIAN, NESTED_NAV_ACTIVE_LINK_CLASSNAME, CALYPSO_LIGHT, CALYPSO);

/**
 * Submenus in the `NavConfigListItem`. These house a primary nav item's
 * (e.g. `API Reference`) secondary-level links. The submenu slides in
 * from the right side of the nav container.
 */
const ULSubMenu = styled(_ref2 => {
  let rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  return /*#__PURE__*/_jsx(UIList, Object.assign({}, rest));
}).withConfig({
  displayName: "NavConfigListItem__ULSubMenu",
  componentId: "sc-1vznng5-1"
})(["position:absolute;top:0;left:100%;z-index:", ";width:100%;", ";opacity:", ";background-color:", ";height:100%;overflow:hidden;overflow-y:auto;"], ({
  active
}) => active ? 1 : -1, ({
  active
}) => active && `transform: translate(-100%);`, ({
  active
}) => active ? 1 : 0, OLAF);

/**
 * The `Title` of the `UIExpandableSection`.
 */
const ExpandableTitle = _ref3 => {
  let {
      titleText
    } = _ref3,
    rest = _objectWithoutPropertiesLoose(_ref3, _excluded3);
  return /*#__PURE__*/_jsx(NavConfigListItemLink, Object.assign({
    LinkComponent: UILink
  }, rest, {
    children: titleText
  }));
};

/**
 * The `Title` of the `UIExpandableSection`.
 */
const ExpandableTitleWithBadge = ({
  titleText,
  badge
}) => {
  return /*#__PURE__*/_jsx(NavConfigListItemLink, {
    LinkComponent: UILink,
    children: /*#__PURE__*/_jsxs(Flex, {
      children: [titleText, /*#__PURE__*/_jsx(View, {
        paddingInlineStart: "200",
        children: getLocalizedBadge(badge)
      })]
    })
  });
};

/**
 * The +/- icons on a group. Indicates that it's an expandable section.
 */
const ExpandableCaret = _ref4 => {
  let {
      open
    } = _ref4,
    rest = _objectWithoutPropertiesLoose(_ref4, _excluded4);
  return /*#__PURE__*/_jsx(UIIcon, Object.assign({}, rest, {
    name: open ? 'minus' : 'add',
    size: 10,
    color: FLINT
  }));
};

/**
 * Link with a localized status badge. Note that folders cannot have badges,
 * which is why this function accepts a `NavPage`.
 */
export const SideNavTitleWithStatusBadge = ({
  item,
  isLocalized
}) => /*#__PURE__*/_jsxs(_Fragment, {
  children: [/*#__PURE__*/_jsx(View, {
    marginInlineEnd: "200",
    as: "span",
    children: item.label
  }), getLocalizedBadge(item.badge), !isLocalized && /*#__PURE__*/_jsx(View, {
    marginInlineStart: "200",
    as: "span",
    children: getLocalizedBadge('EN')
  })]
});

/**
 * The contents of the `UIExpandableSection`.
 */
const ExpandableContentWrapper = styled(UIExpandableContents).withConfig({
  displayName: "NavConfigListItem__ExpandableContentWrapper",
  componentId: "sc-1vznng5-2"
})(["padding-top:12px;padding-bottom:0;padding-right:0;", "{color:", ";font-weight:400;&.", "{font-weight:600;}}"], NavConfigListItemLink, OBSIDIAN, NESTED_NAV_ACTIVE_LINK_CLASSNAME);
const containsFocusedItem = (items, pathname) => items.some(item => {
  if ('items' in item) {
    return containsFocusedItem(item.items, pathname);
  }
  return item.url === pathname;
});
const ExpandableSection = _ref5 => {
  let {
      children,
      item,
      pathname
    } = _ref5,
    rest = _objectWithoutPropertiesLoose(_ref5, _excluded5);
  const [expandableSectionOpen, setExpandableSectionOpenOpen] = useState('items' in item ? containsFocusedItem(item.items, pathname) : false);
  // console.log(item.badge);
  return /*#__PURE__*/_jsx(UIExpandableSection, Object.assign({}, rest, {
    open: expandableSectionOpen,
    Opener: ExpandableOpener,
    Header: ExpandableHeader,
    titleText: item.badge ? /*#__PURE__*/_jsx(ExpandableTitleWithBadge, {
      titleText: item.label,
      badge: item.badge
    }) : /*#__PURE__*/_jsx(ExpandableTitle, {
      titleText: item.label
    }),
    Caret: ExpandableCaret,
    ContentWrapper: ExpandableContentWrapper,
    indentation: "compact",
    onOpenChange: ({
      target: {
        value: isExpandableSectionOpen
      }
    }) => {
      setExpandableSectionOpenOpen(isExpandableSectionOpen);
    },
    children: /*#__PURE__*/_jsx(UIList, {
      children: children
    })
  }));
};
const generatePageNode = (item, locale, userGates) => {
  if (isDocGatedAndDoesUserNotHaveAccess(item, userGates)) {
    return null;
  }
  const isLocalized = isUrlLocalized(item.url, locale);
  const navItemTitle = !isLocalized || item.badge ? /*#__PURE__*/_jsx(SideNavTitleWithStatusBadge, {
    item: item,
    isLocalized: isLocalized
  }) : item.label;
  if (item.url.startsWith('/')) {
    // Internal link

    if (!isLocalized) {
      // This link does not exist in the current locale, so link should point to en-us version
      const englishUrl = getEnglishBaseUrl(item.url);
      return /*#__PURE__*/_jsx(NavConfigListItemLink, {
        LinkComponent: UILink,
        href: englishUrl,
        hrefLang: "en-us",
        external: true,
        children: navItemTitle
      }, item.label);
    }
    return /*#__PURE__*/_jsx(NavConfigListItemLink, {
      LinkComponent: UIRouterNavLink,
      activeClassName: NESTED_NAV_ACTIVE_LINK_CLASSNAME,
      to: item.url,
      children: navItemTitle
    }, item.label);
  } else {
    // External link
    return /*#__PURE__*/_jsx(NavConfigListItemLink, {
      LinkComponent: UILink,
      href: item.url,
      external: true,
      children: navItemTitle
    }, item.label);
  }
};
const generateListItems = (items, pathname, locale, userGates) => items.map(item => {
  // Remove if they do not have access to the gate
  if (isDocGatedAndDoesUserNotHaveAccess(item, userGates)) {
    return null;
  }
  const sharedProps = {
    key: item.label
  };
  if ('items' in item) {
    // NavFolder - Second-level category (an `a[role="button"]` — has children but no href)
    return /*#__PURE__*/_createElement(ExpandableSection, Object.assign({}, sharedProps, {
      item: item,
      pathname: pathname,
      key: item.label
    }), generateListItems(item.items, pathname, locale, userGates));
  }
  // NavPage
  return generatePageNode(item, locale, userGates);
});
const getSubmenuJSX = (items, pathname, locale, userGates) => items.map(item => {
  if (isDocGatedAndDoesUserNotHaveAccess(item, userGates)) {
    return null;
  }
  if (!('items' in item)) {
    // NavPage
    return generatePageNode(item, locale, userGates);
  }

  // NavFolder
  const listItems = generateListItems(item.items, pathname, locale, userGates) || [];
  if (listItems.length) {
    // NavFolder
    return /*#__PURE__*/_jsx(ExpandableSection, {
      item: item,
      pathname: pathname,
      children: /*#__PURE__*/_jsx(UIList, {
        children: listItems
      })
    }, item.label);
  }
  return null;
});

/**
 * Renders a `<li>` containing a title and nested submenu `<ul>` containing
 * the categorized `NavConfig[categories]` links for this site section.
 */
const NavConfigListItem = ({
  navConfig: {
    items
  },
  subMenuFooterNode = null,
  subMenuHeaderNodes = null,
  subMenuProps
}) => {
  const {
    pathname
  } = useLocation();
  const locale = useLocale();
  const userGates = useSelector(selectUserGates);
  const submenuJSX = getSubmenuJSX(items, pathname, locale, userGates.gates);
  return /*#__PURE__*/_jsx("li", {
    children: /*#__PURE__*/_jsxs(ULSubMenu, Object.assign({}, subMenuProps, {
      children: [subMenuHeaderNodes, submenuJSX, subMenuFooterNode]
    }))
  });
};
export default NavConfigListItem;